import type {
  ApiError,
  CaptureLeadRequest,
  CaptureLeadResponse,
  DraftRequest,
  DraftResponse,
  FinalizeRequest,
  FinalizeResponse,
  GenerateMonthRequest,
  GenerateMonthResponse,
  StoredPlan,
} from '@content-planner/shared';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';
const BASE_URL = API_BASE_URL;

export class ApiClientError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(init.headers ?? {}),
    },
  });

  if (!res.ok) {
    let code = 'unknown';
    let message = `Request failed with status ${res.status}.`;
    try {
      const body = (await res.json()) as ApiError;
      code = body.error.code;
      message = body.error.message;
    } catch {
      // body wasn't JSON; keep generic message
    }
    throw new ApiClientError(res.status, code, message);
  }
  return (await res.json()) as T;
}

export const api = {
  generateMonth(body: GenerateMonthRequest): Promise<GenerateMonthResponse> {
    return request<GenerateMonthResponse>('/api/generate-month', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  draft(body: DraftRequest): Promise<DraftResponse> {
    return request<DraftResponse>('/api/draft', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  captureLead(body: CaptureLeadRequest): Promise<CaptureLeadResponse> {
    return request<CaptureLeadResponse>('/api/capture-lead', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  finalize(body: FinalizeRequest, token: string): Promise<FinalizeResponse> {
    return request<FinalizeResponse>('/api/finalize', {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  getPlan(planId: string): Promise<StoredPlan> {
    return request<StoredPlan>(`/api/plan/${planId}`, { method: 'GET' });
  },
};

/** Absolute URL for the downloadable PDF of a stored plan. */
export function planPdfUrl(planId: string): string {
  return `${API_BASE_URL}/api/plan/${planId}/pdf`;
}
